<script setup lang="ts">
import { computed } from "vue";

/**
 * AI 출력 경량 서식(70차 UXD-01). 모델 출력은 신뢰할 수 없어 `v-html` 을 쓰지 않고 **텍스트 노드만** 그린다.
 * - 빈 줄 = 문단 나눔. 문단 안 줄바꿈은 그대로 둔다(`white-space: pre-line`).
 * - 줄 앞 `- ` · `• ` = 점 목록, `1. ` = 번호 목록(첫 번호를 `start` 로 이어 간다). 목록 항목 아래 들여쓴 `- ` 는 한 단계 하위 목록,
 *   들여쓴 그 밖의 줄은 앞 항목에 이어 붙인다.
 * - `**굵게**` = `<strong>`. 닫히지 않은 `**` 는 그대로 둔다.
 * - 줄 앞 `#` ~ `######` 머리 기호는 떼고 굵은 문단으로 둔다. 그 밖의 기호(표 · 코드 표시 등)는 손대지 않는다.
 * - 복사 · 적용은 호출부가 원문 그대로 한다(이 부품은 보이기만 한다).
 */
const props = withDefaults(
  defineProps<{
    /** AI 출력 원문 */
    text: string;
    /** sm = 13px(말풍선 · 검토 패널), md = 14px(본문) */
    size?: "sm" | "md";
  }>(),
  { size: "md" },
);

/** 굵게 여부가 붙은 글 조각 */
interface Segment {
  text: string;
  strong: boolean;
}

interface ListItem {
  segments: Segment[];
  children: Segment[][];
}

type ListBlock =
  | { kind: "ul"; items: ListItem[] }
  | { kind: "ol"; start: number; items: ListItem[] };

type Block = { kind: "p"; segments: Segment[]; strong: boolean } | ListBlock;

const HEADING = /^\s{0,3}#{1,6}\s+/;
const BULLET = /^(\s*)[-•]\s+(.*)$/;
const ORDERED = /^(\s*)(\d{1,3})\.\s+(.*)$/;
const STRONG = /\*\*(.+?)\*\*/g;

/** `**x**` 를 굵은 조각으로 나눈다. 짝이 없는 `**` 는 글자 그대로 남는다. */
function segmentsOf(text: string): Segment[] {
  const segments: Segment[] = [];
  let last = 0;
  for (const match of text.matchAll(STRONG)) {
    const index = match.index ?? 0;
    if (index > last) {
      segments.push({ text: text.slice(last, index), strong: false });
    }
    segments.push({ text: match[1] ?? "", strong: true });
    last = index + match[0].length;
  }
  if (last < text.length) {
    segments.push({ text: text.slice(last), strong: false });
  }
  return segments;
}

/** 이어 붙이는 줄은 줄바꿈과 함께 마지막 조각 뒤에 붙인다 */
function appendLine(segments: Segment[], line: string): void {
  segments.push({ text: "\n", strong: false }, ...segmentsOf(line));
}

function parse(source: string): Block[] {
  const blocks: Block[] = [];
  let paragraph: string[] = [];
  let list = null as ListBlock | null;

  const flushParagraph = () => {
    if (paragraph.length) {
      blocks.push({
        kind: "p",
        segments: segmentsOf(paragraph.join("\n")),
        strong: false,
      });
      paragraph = [];
    }
  };

  for (const raw of source.replace(/\r\n?/g, "\n").split("\n")) {
    const line = raw.replace(/\s+$/, "");
    if (!line.trim()) {
      flushParagraph();
      list = null;
      continue;
    }

    const bullet = BULLET.exec(line);
    const ordered = bullet ? null : ORDERED.exec(line);
    const lastItem = list?.items.at(-1);

    // 목록 항목 아래 들여쓴 줄: 점 목록이면 하위 항목, 그 밖은 앞 항목에 이어 붙인다.
    if (lastItem && /^\s{2,}/.test(line)) {
      if (bullet) {
        lastItem.children.push(segmentsOf(bullet[2] ?? ""));
      } else {
        appendLine(lastItem.children.at(-1) ?? lastItem.segments, line.trim());
      }
      continue;
    }

    if (bullet || ordered) {
      flushParagraph();
      const kind = bullet ? "ul" : "ol";
      if (list?.kind !== kind) {
        list =
          kind === "ul"
            ? { kind, items: [] }
            : { kind, start: Number(ordered?.[2] ?? 1), items: [] };
        blocks.push(list);
      }
      list.items.push({
        segments: segmentsOf((bullet ? bullet[2] : ordered?.[3]) ?? ""),
        children: [],
      });
      continue;
    }

    list = null;
    if (HEADING.test(line)) {
      flushParagraph();
      blocks.push({
        kind: "p",
        segments: segmentsOf(line.replace(HEADING, "")),
        strong: true,
      });
      continue;
    }
    paragraph.push(line.trim());
  }
  flushParagraph();
  return blocks;
}

const blocks = computed(() => parse(props.text ?? ""));
</script>

<template>
  <div class="ai-text" :class="`is-${size}`">
    <template v-for="(block, blockIndex) in blocks" :key="blockIndex">
      <p v-if="block.kind === 'p'" :class="{ 'is-strong': block.strong }">
        <template
          v-for="(segment, index) in block.segments"
          :key="`${blockIndex}-${index}`"
        >
          <strong v-if="segment.strong">{{ segment.text }}</strong>
          <template v-else>{{ segment.text }}</template>
        </template>
      </p>
      <component
        :is="block.kind"
        v-else
        :start="
          block.kind === 'ol' && block.start !== 1 ? block.start : undefined
        "
      >
        <li v-for="(item, itemIndex) in block.items" :key="itemIndex">
          <template
            v-for="(segment, index) in item.segments"
            :key="`${itemIndex}-${index}`"
          >
            <strong v-if="segment.strong">{{ segment.text }}</strong>
            <template v-else>{{ segment.text }}</template>
          </template>
          <ul v-if="item.children.length">
            <li v-for="(child, childIndex) in item.children" :key="childIndex">
              <template
                v-for="(segment, index) in child"
                :key="`${childIndex}-${index}`"
              >
                <strong v-if="segment.strong">{{ segment.text }}</strong>
                <template v-else>{{ segment.text }}</template>
              </template>
            </li>
          </ul>
        </li>
      </component>
    </template>
  </div>
</template>

<style scoped>
.ai-text {
  display: grid;
  gap: var(--ui-gap-sm);
  color: var(--ui-text);
  line-height: 1.6;
  overflow-wrap: anywhere;
  word-break: keep-all;
}

.ai-text.is-sm {
  font-size: var(--ui-fs-sm);
}

.ai-text.is-md {
  font-size: var(--ui-fs-md);
}

.ai-text :is(p, ul, ol) {
  margin: 0;
}

.ai-text p,
.ai-text li {
  white-space: pre-line;
}

.ai-text p.is-strong {
  font-weight: 800;
}

.ai-text strong {
  font-weight: 800;
}

.ai-text :is(ul, ol) {
  display: grid;
  gap: var(--ui-gap-2xs);
  padding-left: 1.4em;
}

.ai-text li::marker {
  color: var(--ui-text-3);
}

.ai-text li > ul {
  margin-top: var(--ui-gap-2xs);
}
</style>
